/**
 * Heuristic AI replacement-risk scoring, shared by AIRiskPreview and the
 * UnderpaidChecker AI Outlook block. Matches the PDF generation logic.
 */

export interface AIRiskResult {
  riskScore: number;
  riskLevel: 'Low' | 'Moderate' | 'High' | 'Very High';
  timeline: {
    nearTerm: string;
    mediumTerm: string;
    longTerm: string;
  };
  factors: string[];
}

export function calculateAIRiskScore(socCode: string, occupationTitle: string): AIRiskResult {
  const socMajor = socCode.split('-')[0];
  let baseScore = 0;
  const factors: string[] = [];

  const blueCollarSOCs = ['33', '35', '37', '45', '47', '49', '51', '53'];
  const whiteCollarSOCs = ['11', '13', '15', '17', '19', '21', '23', '25', '27', '29', '31', '41', '43'];

  if (blueCollarSOCs.includes(socMajor)) {
    baseScore = 20;
    factors.push('Physical presence and manual skills required');
  } else if (whiteCollarSOCs.includes(socMajor)) {
    baseScore = 50;
    factors.push('Knowledge-based work - moderate automation risk');
  } else {
    baseScore = 40;
  }

  if (socMajor === '43') {
    baseScore = 75;
    factors.push('Routine administrative tasks');
  } else if (socMajor === '15') {
    baseScore = 45;
    factors.push('AI augmentation likely, full replacement less likely');
  } else if (socMajor === '29' || socMajor === '25') {
    baseScore = 15;
    factors.push('High human interaction requirement');
  } else if (socMajor === '27') {
    baseScore = 20;
    factors.push('Creative and subjective work');
  } else if (socMajor === '13') {
    baseScore = 60;
    factors.push('Data-driven decision making');
  }

  const titleLower = occupationTitle.toLowerCase();
  if (titleLower.includes('data entry') || titleLower.includes('clerk')) {
    baseScore += 20;
    factors.push('Highly routine tasks');
  }
  if (titleLower.includes('analyst') && !titleLower.includes('research')) {
    baseScore += 15;
    factors.push('Pattern-based analysis');
  }
  if (titleLower.includes('nurse') || titleLower.includes('teacher') || titleLower.includes('therapist')) {
    baseScore -= 25;
    factors.push('Empathy and human connection required');
  }

  baseScore = Math.max(0, Math.min(100, baseScore));

  let riskLevel: 'Low' | 'Moderate' | 'High' | 'Very High';
  if (baseScore < 30) riskLevel = 'Low';
  else if (baseScore < 50) riskLevel = 'Moderate';
  else if (baseScore < 75) riskLevel = 'High';
  else riskLevel = 'Very High';

  const timeline = {
    nearTerm: baseScore > 70 ? 'Moderate' : baseScore > 40 ? 'Low' : 'Minimal',
    mediumTerm: baseScore > 70 ? 'High' : baseScore > 40 ? 'Moderate' : 'Low',
    longTerm: baseScore > 50 ? 'High' : baseScore > 30 ? 'Moderate' : 'Low',
  };

  return { riskScore: baseScore, riskLevel, timeline, factors };
}
